import React, { useState, useEffect } from 'react';
import { Event } from '../../types/Event';
import { getLocationDisplayName } from '../../utils/locationUtils';
import { AdminFormProps } from '../shared/AdminInterfaces';

interface EventFormData {
  title: string;
  description: string;
  startDateTime: string;
  endDateTime: string;
  location: string;
  volunteersNeeded: boolean;
  maxVolunteers?: number;
  volunteerDescription?: string;
  privateEvent: boolean;
}

const EventForm: React.FC<AdminFormProps<Event, EventFormData>> = ({
  item,
  isOpen,
  onSubmit,
  onClose,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    startDateTime: '',
    endDateTime: '',
    location: '',
    volunteersNeeded: false,
    maxVolunteers: 0,
    volunteerDescription: '',
    privateEvent: false
  });

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title,
        description: item.description,
        startDateTime: new Date(item.startDateTime).toISOString().slice(0, 16),
        endDateTime: new Date(item.endDateTime).toISOString().slice(0, 16),
        location: getLocationDisplayName(item.location),
        volunteersNeeded: item.volunteersNeeded,
        maxVolunteers: item.maxVolunteers || 0,
        volunteerDescription: item.volunteerDescription || '',
        privateEvent: item.privateEvent || false
      });
    } else {
      // Reset for create mode
      setFormData({
        title: '',
        description: '',
        startDateTime: '',
        endDateTime: '',
        location: '',
        volunteersNeeded: false,
        maxVolunteers: 0,
        volunteerDescription: '',
        privateEvent: false
      });
    }
  }, [item]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const eventData = {
      ...formData,
      maxVolunteers: formData.volunteersNeeded ? formData.maxVolunteers : undefined
    };
    onSubmit(eventData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {item ? 'Edit Event' : 'Add New Event'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={3}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Date/Time</label>
              <input
                type="datetime-local"
                name="startDateTime"
                value={formData.startDateTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date/Time</label>
              <input
                type="datetime-local"
                name="endDateTime"
                value={formData.endDateTime}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="volunteersNeeded"
                checked={formData.volunteersNeeded}
                onChange={handleInputChange}
                className="mr-2 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Volunteers Needed</span>
            </label>

            {formData.volunteersNeeded && (
              <div className="ml-6 space-y-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Maximum Volunteers</label>
                  <input
                    type="number"
                    name="maxVolunteers"
                    value={formData.maxVolunteers}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Volunteer Description</label>
                  <textarea
                    name="volunteerDescription"
                    value={formData.volunteerDescription}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe what volunteers will do..."
                  />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="privateEvent"
                checked={formData.privateEvent}
                onChange={handleInputChange}
                className="mr-2 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Private Event</span>
            </label>
            <p className="text-xs text-gray-600 ml-6">
              Private events are only visible to logged-in users. Uncheck to make this event visible to all visitors.
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : (item ? 'Update Event' : 'Create Event')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm;
